import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatRelativeTime } from '../utils/relativeTime';

/**
 * Small caption that shows how long ago the library was last synced
 * from Drive, plus an icon-only manual "Refresh" button that forces a
 * fresh Drive re-fetch. Renders nothing when no sync has ever happened
 * (fresh install) or in local mode (where "sync" isn't meaningful).
 *
 * Auto-refreshes the phrasing every 30 seconds so "just now" →
 * "a moment ago" → "5 min ago" all happen without an external
 * re-render.
 *
 * Props:
 *  - lastSyncedAt: number|null timestamp of the last successful sync.
 *  - isLocalMode: bool, hides the whole chip in local mode.
 *  - onRefresh: () => Promise|void, forces a Drive re-fetch.
 *  - refreshing: bool, in-flight state (spinner + disabled button).
 *  - isOffline: bool, when true the tap shows a toast instead of fetching.
 *  - t: i18n translator (optional).
 */
export const LastSyncedChip = ({
  lastSyncedAt,
  isLocalMode,
  onRefresh,
  refreshing = false,
  isOffline = false,
  t,
}) => {
  // Bump on a timer so the relative phrasing stays truthful without
  // needing an external re-render. Cheap: one interval, no listeners.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  if (isLocalMode) return null;
  if (!lastSyncedAt) return null;

  const tr = (key, fallback) => (t && t(key)) || fallback;

  const handleClick = () => {
    if (refreshing) return;
    if (isOffline) {
      // Surface the same offline messaging the write-guard uses.
      toast.error(tr('refreshOffline', 'You\u2019re offline \u2014 reconnect to refresh.'));
      return;
    }
    if (onRefresh) onRefresh();
  };

  return (
    <div
      className="mt-2 flex items-center gap-1.5 text-xs"
      style={{ color: 'hsl(125, 12%, 32%)' }}
      data-testid="last-synced-chip"
    >
      <span role="status" aria-live="polite">
        {tr('lastSyncedLabel', 'Last synced')} {formatRelativeTime(lastSyncedAt)}
      </span>
      <button
        type="button"
        onClick={handleClick}
        disabled={refreshing}
        title={refreshing ? tr('refreshSyncing', 'Refreshing…') : tr('refreshNow', 'Refresh now')}
        aria-label={refreshing ? tr('refreshSyncing', 'Refreshing…') : tr('refreshNow', 'Refresh now')}
        aria-busy={refreshing}
        className="inline-flex items-center justify-center min-w-[24px] min-h-[24px] rounded-full p-1.5 transition-colors hover:bg-green-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600/50 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ color: 'hsl(143, 28%, 34%)' }}
        data-testid="last-synced-refresh-button"
      >
        <RefreshCw
          className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

export default LastSyncedChip;
